import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, ShieldCheck, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { requestOTP, verifyOTP } from '../api/insights';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { trackLogin, trackSignupCompleted } from '../analytics/mixpanel';

const getProfileSetupRequiredKey = (userLike) => {
    const suffix = String(userLike?.id || userLike?.email || 'anonymous');
    return `insights_profile_setup_required:${suffix}`;
};

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
    const { login } = useAuth();
    const { isDark } = useTheme();
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState('email'); // 'email' or 'otp'
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [resendCount, setResendCount] = useState(0);

    useEffect(() => {
        let timer;
        if (resendTimer > 0) {
            timer = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendTimer]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setEmail('');
            setOtp('');
            setStep('email');
            setLoading(false);
            setResendTimer(0);
            setResendCount(0);
        }
    }, [isOpen]);

    const handleRequestOTP = async (e) => {
        if (e) e.preventDefault();
        if (!email) return toast.error('Please enter your email');

        setLoading(true);
        try {
            await requestOTP(email);
            setStep('otp');
            setResendTimer(60);
            toast.success('OTP sent to your email');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (resendTimer > 0) return;
        if (resendCount >= 5) return toast.error('Maximum resend attempts reached');

        setLoading(true);
        try {
            await requestOTP(email);
            setResendTimer(60);
            setResendCount((prev) => prev + 1);
            toast.success('OTP resent successfully');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        if (!otp) return toast.error('Please enter the OTP');

        setLoading(true);
        try {
            const source = localStorage.getItem('insights_referral_source') || '';
            const data = await verifyOTP(email, otp, source || null);
            const hasPendingAction = typeof onSuccess === 'function';
            if (data.is_new_user) {
                trackSignupCompleted('email');
                login(data.user, data.token);
                try {
                    localStorage.setItem(getProfileSetupRequiredKey(data.user), '1');
                } catch (_) { }
                if (hasPendingAction) {
                    toast.success('Welcome! Continuing your previous action.');
                    onSuccess?.({ isNewUser: true, user: data.user });
                } else {
                    toast.success('Welcome! You are logged in.');
                }
                onClose();
            } else {
                trackLogin();
                login(data.user, data.token);
                if (hasPendingAction) {
                    toast.success('Logged in. Continuing your previous action.');
                    onSuccess?.({ isNewUser: false, user: data.user });
                } else {
                    toast.success('Logged in successfully');
                }
                onClose();
            }
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const accent = isDark ? '#F59E0B' : '#2563eb';
    const bg = isDark ? '#0d1019' : '#ffffff';
    const containerBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(37,99,235,0.15)';
    const textPrimary = isDark ? 'rgba(255,255,255,0.92)' : '#0f172a';
    const textSecondary = isDark ? 'rgba(255,255,255,0.45)' : '#475569';
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
        color: isDark ? 'rgba(255,255,255,0.88)' : '#1e293b',
    };
    const inputFocusHandler = (e) => {
        e.target.style.borderColor = isDark ? 'rgba(245,158,11,0.55)' : 'rgba(37,99,235,0.55)';
        e.target.style.boxShadow = isDark ? '0 0 0 3px rgba(245,158,11,0.1)' : '0 0 0 3px rgba(37,99,235,0.1)';
    };
    const inputBlurHandler = (e) => {
        e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0';
        e.target.style.boxShadow = 'none';
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 backdrop-blur-md"
                    style={{ background: isDark ? 'rgba(5,4,3,0.75)' : 'rgba(241,245,249,0.8)' }}
                />

                {/* Modal Container */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl"
                    style={{ background: bg, border: `1px solid ${containerBorder}` }}
                >
                    {/* Subtle glow top */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 pointer-events-none"
                        style={{ background: isDark ? 'radial-gradient(ellipse at center, rgba(245,158,11,0.12) 0%, transparent 70%)' : 'radial-gradient(ellipse at center, rgba(37,99,235,0.08) 0%, transparent 70%)' }} />

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute right-5 top-5 p-2 rounded-full transition-all"
                        style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b', background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9' }}
                        onMouseEnter={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.8)' : '#0f172a'}
                        onMouseLeave={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.4)' : '#64748b'}
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="p-8 sm:p-10 pt-10 sm:pt-12">
                        <div className="mb-8 text-center">
                            {/* Icon badge */}
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5"
                                style={{
                                    background: isDark ? 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(217,119,6,0.12))' : 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(37,99,235,0.04))',
                                    border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)'}`
                                }}>
                                {step === 'email'
                                    ? <Mail className="w-7 h-7" style={{ color: accent }} />
                                    : <ShieldCheck className="w-7 h-7" style={{ color: accent }} />
                                }
                            </div>
                            <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: textPrimary }}>
                                {step === 'email' ? 'Welcome Back' : 'Security Check'}
                            </h2>
                            <p style={{ color: textSecondary, fontSize: '0.9rem' }}>
                                {step === 'email'
                                    ? 'Enter your email to sign in or create an account'
                                    : `We sent a code to ${email}`}
                            </p>
                        </div>

                        <AnimatePresence mode="wait">
                            {step === 'email' ? (
                                <motion.form
                                    key="email-step"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    onSubmit={handleRequestOTP}
                                    className="space-y-5"
                                >
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-medium" style={{ color: textSecondary }}>Email Address</label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }} />
                                            <input
                                                type="email"
                                                required
                                                placeholder="name@example.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onFocus={inputFocusHandler}
                                                onBlur={inputBlurHandler}
                                                className="w-full h-14 pl-11 pr-4 rounded-2xl text-sm transition-all outline-none"
                                                style={{ ...inputStyle, fontFamily: 'inherit' }}
                                                data-testid="email-input"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-14 rounded-full font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                                        style={{ background: isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: isDark ? '#1a1612' : '#ffffff' }}
                                        data-testid="request-otp-button"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Request OTP <ArrowRight className="w-4 h-4" /></>}
                                    </button>
                                </motion.form>
                            ) : (
                                <motion.form
                                    key="otp-step"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    onSubmit={handleVerifyOTP}
                                    className="space-y-5"
                                >
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-medium" style={{ color: textSecondary }}>Verification Code</label>
                                        <div className="relative">
                                            <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }} />
                                            <input
                                                type="text"
                                                required
                                                maxLength={6}
                                                placeholder="000000"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                                onFocus={inputFocusHandler}
                                                onBlur={inputBlurHandler}
                                                className="w-full h-14 pl-11 pr-4 rounded-2xl text-2xl tracking-[0.5em] font-mono transition-all outline-none"
                                                style={inputStyle}
                                                data-testid="otp-input"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-14 rounded-full font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                                        style={{ background: isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: isDark ? '#1a1612' : '#ffffff' }}
                                        data-testid="verify-otp-button"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                                    </button>
                                    <div className="flex flex-col items-center gap-1.5 pt-1">
                                        <button
                                            type="button"
                                            disabled={resendTimer > 0 || loading || resendCount >= 5}
                                            onClick={handleResendOTP}
                                            className="text-sm flex items-center gap-2 disabled:opacity-40 transition-colors"
                                            style={{ color: accent }}
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                                            {resendTimer > 0 ? `Resend Code in ${resendTimer}s` : 'Resend Code'}
                                        </button>
                                        {resendCount > 0 && resendCount < 5 && (
                                            <span className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>{resendCount}/5 attempts used</span>
                                        )}
                                        {resendCount >= 5 && (
                                            <span className="text-xs" style={{ color: '#ef4444' }}>Maximum resend limit reached</span>
                                        )}
                                        <p className="text-sm font-medium text-center mt-1" style={{ color: textSecondary }}>
                                            Didn't find it? Check your spam folder before retrying.
                                        </p>
                                    </div>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default AuthModal;
